import { useState, useCallback, useEffect, useRef } from 'react';
import { wattelEMApi, type DeviceOverview, type HistoryPoint, type DailyEnergy } from '../api/wattelEM';

// ============================================
// State
// ============================================

interface WattelEMState {
  devices: DeviceOverview[];
  selectedSn: string | null;
  history: HistoryPoint[];
  dailyEnergy: DailyEnergy[];
  loading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  relayPending: boolean;
}

const POLL_INTERVAL_MS = 5000;
/** Plafond du backoff : 5s * 12 = 60s entre deux tentatives. */
const MAX_BACKOFF_FACTOR = 12;

const INITIAL_STATE: WattelEMState = {
  devices: [],
  selectedSn: null,
  history: [],
  dailyEnergy: [],
  loading: false,
  error: null,
  lastUpdate: null,
  relayPending: false,
};

// ============================================
// Hook
// ============================================

export function useWattelEM() {
  const [state, setState] = useState<WattelEMState>(INITIAL_STATE);
  const mountedRef = useRef(true);

  // ── Pending relay commands ────────────────────────────────────────────────
  // Map sn -> { value, at } : commandes envoyées dont on attend la confirmation.
  // Tant que la DB renvoie une valeur DIFFÉRENTE de notre commande ET qu'elle
  // a < 10s, on considère que c'est un retard MQTT et on garde notre valeur
  // optimiste pour éviter le flicker (OFF → ON → OFF).
  // Quand la DB confirme (même valeur) OU au bout de 10s, on libère.
  const pendingRelayRef = useRef(new Map<string, { value: 0 | 1; at: number }>());
  const PENDING_TIMEOUT_MS = 10_000;

  // ── Anti-empilement du polling ────────────────────────────────────────────
  // Un seul /overview en vol à la fois. Sans ce garde-fou, quand le backend
  // ralentit, le tick de 5s continue d'émettre des requêtes qui ne reviennent
  // jamais : elles saturent le pool MySQL côté serveur et plus rien ne répond
  // (le AbortController du client n'annule QUE l'attente, pas le travail SQL).
  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  // Backoff exponentiel après échec : 5s → 10s → 20s → 40s → 60s (plafond).
  const failuresRef = useRef(0);

  // Load overview (all devices + telemetry + zones)
  const loadOverview = useCallback(async (showLoading = false) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;
    if (showLoading) setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const devices = await wattelEMApi.getOverview(controller.signal);
      if (!mountedRef.current) return;
      failuresRef.current = 0;
      // ── Réconciliation pending vs DB ──
      const now = Date.now();
      const reconciled = devices.map(d => {
        const pending = pendingRelayRef.current.get(d.device_sn);
        if (!pending) return d;
        const age = now - pending.at;
        // DB a confirmé OU pending expiré → on libère et on prend la DB
        if (d.relay_state === pending.value || age > PENDING_TIMEOUT_MS) {
          pendingRelayRef.current.delete(d.device_sn);
          return d;
        }
        // Pending récent + DB pas encore synchro → on garde la valeur optimiste
        return { ...d, relay_state: pending.value, relay_cmd: pending.value };
      });
      setState(prev => ({
        ...prev,
        devices: reconciled,
        loading: false,
        error: null,
        lastUpdate: new Date(),
      }));
    } catch (err) {
      // Annulation au démontage : on ne touche pas au state.
      if (!mountedRef.current || controller.signal.aborted) return;
      failuresRef.current += 1;
      setState(prev => ({
        ...prev,
        loading: false,
        error: (err as Error).message,
      }));
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Select a device and load its history + daily energy
  const selectDevice = useCallback(async (sn: string) => {
    setState(prev => ({ ...prev, selectedSn: sn, history: [], dailyEnergy: [] }));
    const results = await Promise.allSettled([
      wattelEMApi.getHistory(sn, 24, 500),
      wattelEMApi.getDailyEnergy(sn, 90),
    ]);
    if (!mountedRef.current) return;
    const history = results[0].status === 'fulfilled' ? results[0].value : [];
    const dailyEnergy = results[1].status === 'fulfilled' ? results[1].value : [];
    setState(prev => (prev.selectedSn === sn ? { ...prev, history, dailyEnergy } : prev));
  }, []);

  const deselectDevice = useCallback(() => {
    setState(prev => ({ ...prev, selectedSn: null, history: [], dailyEnergy: [] }));
  }, []);

  // Relay control
  // opts.source='schedule' : envoi automatique de l'exécuteur de planning. Un
  // refus (planning modifié ailleurs entre-temps) n'est pas une erreur à
  // afficher à l'utilisateur : on se contente de resynchroniser l'état.
  const sendRelay = useCallback(async (sn: string, value: 0 | 1, opts?: { source?: 'schedule' }) => {
    setState(prev => ({ ...prev, relayPending: true }));
    // ⚡ Optimistic update + enregistrement du pending pour réconciliation
    pendingRelayRef.current.set(sn, { value, at: Date.now() });
    setState(prev => ({
      ...prev,
      devices: prev.devices.map(d =>
        d.device_sn === sn ? { ...d, relay_state: value, relay_cmd: value } : d
      ),
    }));
    try {
      await wattelEMApi.setRelay(sn, value, opts?.source);
      // Le polling 5s + la réconciliation pending s'occupent du reste.
      // Pas de reload immédiat ici : ça créait un setState avec données DB
      // obsolètes qui écrasait l'optimiste.
    } catch (err) {
      // Échec : on libère le pending pour ne pas masquer l'état réel
      pendingRelayRef.current.delete(sn);
      if (opts?.source === 'schedule') {
        console.warn(`[Schedule] commande refusée pour ${sn} :`, (err as Error).message);
      } else {
        setState(prev => ({ ...prev, error: (err as Error).message }));
      }
      loadOverview();
    } finally {
      if (mountedRef.current) setState(prev => ({ ...prev, relayPending: false }));
    }
  }, [loadOverview]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    loadOverview(true);
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [loadOverview]);

  // Polling toutes les 5s, replanifié APRÈS chaque réponse (pas setInterval) :
  // deux requêtes ne peuvent jamais se chevaucher. En cas d'échec répété, le
  // délai double jusqu'à 60s pour laisser le backend se rétablir.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const nextDelay = () =>
      POLL_INTERVAL_MS * Math.min(2 ** failuresRef.current, MAX_BACKOFF_FACTOR);
    const tick = async () => {
      await loadOverview(false);
      if (!mountedRef.current) return;
      timer = setTimeout(tick, nextDelay());
    };
    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [loadOverview]);

  // Get the selected device from the list
  const selectedDevice = state.devices.find(d => d.device_sn === state.selectedSn) || null;

  return {
    devices: state.devices,
    selectedDevice,
    selectedSn: state.selectedSn,
    history: state.history,
    dailyEnergy: state.dailyEnergy,
    loading: state.loading,
    error: state.error,
    lastUpdate: state.lastUpdate,
    relayPending: state.relayPending,
    loadOverview: () => loadOverview(true),
    selectDevice,
    deselectDevice,
    sendRelay,
  };
}

export default useWattelEM;
